import { Text } from 'components';
import { theme } from 'lib';
import styled from 'styled-components';

// Shared atoms for the FanWave timeline editor. The one convention worth stating in one place is
// OBSERVED vs PROJECTED: a time is observed when the match actually reached that moment (the
// backend stamps each phase transition on the server clock as it sees it) and projected when it
// has not got there yet. A projection WILL MOVE, so it is drawn dashed and quoted with a "~".
// An admin who sets half-time +0 and reads a bare "20:18" will otherwise take it for a promise.

/** Solid for a fact, dashed for a projection. Used by the phase strip, the track and the labels. */
export const edgeStyle = (observed) => (observed ? 'solid' : 'dashed');

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
const dayTimeFmt = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});
const dayFmt = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric'
});

const asDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "20:18" — the clock time, in the admin's own timezone. */
export const fmtTime = (value) => {
  const d = asDate(value);
  return d ? timeFmt.format(d) : '—';
};

/** "Aug 24, 20:18" — for anything that may not be today. */
export const fmtDateTime = (value) => {
  const d = asDate(value);
  return d ? dayTimeFmt.format(d) : '—';
};

/** "Mon, Aug 24" — calendar-zoom column headers. */
export const fmtDay = (value) => {
  const d = asDate(value);
  return d ? dayFmt.format(d) : '—';
};

/**
 * A time with its certainty attached: "20:18" when observed, "~20:18" when projected.
 *
 * The tilde is not decoration. It is the difference between "this wave opened at 20:18" and "on
 * current estimates this wave will open around 20:18, and that estimate moves every time the
 * match feed reports something".
 */
export const fmtMaybe = (value, observed, fmt = fmtTime) =>
  `${observed ? '' : '~'}${fmt(value)}`;

export const localTzLabel = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'local time';
  }
})();

/** "22 min", "1h 10m" — a span's length, for gap slots and segment tooltips. */
export const fmtSpan = (minutes) => {
  const n = Math.round(Number(minutes) || 0);
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

// Matches the create form's date field so the two read as one form while both exist.
export const DateInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? theme.SECONDARY : theme.GREY_30)};
  padding: 0 14px;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_80};
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.SECONDARY};
  }

  &:disabled {
    background: ${theme.GREY_10};
    color: ${theme.GREY_60};
    cursor: default;
  }
`;

// Native select sized to sit flush beside `DateInput` — same 44px height and 8px radius. The
// shared `Dropdown` is a 480px overlay built for filter bars and far too heavy for an inline
// four-option kind switch.
export const Select = styled.select`
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? theme.SECONDARY : theme.GREY_30)};
  padding: 0 12px;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_80};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.SECONDARY};
  }

  &:disabled {
    background: ${theme.GREY_10};
    color: ${theme.GREY_60};
    cursor: default;
  }
`;

export const NumberInput = styled(DateInput).attrs({ type: 'number', inputMode: 'numeric' })``;

/** Field-level error text — same red-text convention as the Quiz/Polls forms. */
export const FieldError = ({ children }) =>
  children ? (
    <Text c1 col={theme.SECONDARY} marg="5px 0 0" style={{ fontSize: 11.5, lineHeight: 1.4 }}>
      {children}
    </Text>
  ) : null;

export const FieldHint = ({ children }) =>
  children ? (
    <Text c1 col={theme.GREY_60} marg="5px 0 0" style={{ fontSize: 11.5, lineHeight: 1.4 }}>
      {children}
    </Text>
  ) : null;

/** Tone → the colours a notice or conflict banner is painted in. */
export const NOTICE_TONE = {
  danger: { bg: '#FEF2F2', border: '#FECACA', col: '#991B1B' },
  warning: { bg: '#FFFBEB', border: '#FDE68A', col: '#92400E' },
  info: { bg: '#EFF6FF', border: '#BFDBFE', col: '#1E40AF' },
  neutral: { bg: theme.GREY_10, border: theme.GREY_20, col: theme.GREY_70 }
};

export const Notice = ({ tone = 'info', title, children, right }) => {
  const t = NOTICE_TONE[tone] || NOTICE_TONE.info;
  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 13px',
        borderRadius: 10,
        background: t.bg,
        border: `1px solid ${t.border}`,
        boxSizing: 'border-box'
      }}
    >
      <div style={{ minWidth: 0 }}>
        {title && (
          <Text b2 bold col={t.col} marg="0" style={{ fontSize: 12.5, lineHeight: 1.35 }}>
            {title}
          </Text>
        )}
        {children && (
          <Text c1 col={t.col} marg={title ? '3px 0 0' : '0'} style={{ fontSize: 11.5, lineHeight: 1.45 }}>
            {children}
          </Text>
        )}
      </div>
      {right}
    </div>
  );
};
